import {
	useCreateProjectAdditionalPositionInformationMutation,
	type CreateProjectAdditionalPositionInformationMutationVariables,
	useUpdateProjectAdditionalPositionInformationMutation,
	type UpdateProjectAdditionalPositionInformationMutationVariables,
	ProjectPositionPriceRateType,
} from "@repo/server-api";
import { useNotifier, type ServerError } from "@repo/shared/common";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ProjectPosition } from "../types/ProjectTypes";

type UseProjectAdditionalPositionInfoOptions = {
	projectId: string;
	onSaved: (position: ProjectPosition) => void;
};

type UseProjectAdditionalPositionInfoResult = {
	saveProjectPosition: (position: ProjectPosition) => void;
	loading: boolean;
};

type UpsertResult = {
	data: ProjectPosition | null;
	errors: ServerError[] | null;
	exception: unknown | null;
};

// Here is the hook
export function useSaveProjectAdditionalPositionInformation(
	options: UseProjectAdditionalPositionInfoOptions,
): UseProjectAdditionalPositionInfoResult {
	const { t } = useTranslation();

	const { notifySuccess, notifyErrors, notifyException } = useNotifier();

	const { projectId, onSaved } = options;
	const [loading, setLoading] = useState(false);

	// Update Mutation
	const [updateProjectPosition] = useUpdateProjectAdditionalPositionInformationMutation();

	const updatePosition = async (position: ProjectPosition): Promise<UpsertResult> => {
		if (position.service == null) {
			throw new Error(t("validation-error"));
		}

		const vars: UpdateProjectAdditionalPositionInformationMutationVariables = {
			hours: position.hours ?? null,
			pricePerHour: position.pricePerHour ?? null,
			projectId,
			saleableServiceId: position.service.id,
			serviceName: position.service.name,
			servicePrice: position.service.price,
			informationId: position.id,
			price: position.price ?? null,
			description: position.description,
			accountingProfileId: position.accountingProfile?.id ?? null,
			priceRate: (position.priceRate as ProjectPositionPriceRateType) ?? null,
		};
		const result = await updateProjectPosition({ variables: vars });

		// Return the updated position if successful
		const resultData = result.data?.updateProjectAdditionalPositionInformation
			?.additionalPositionInformation
			? position
			: null;

		return {
			data: resultData,
			errors: result.data?.updateProjectAdditionalPositionInformation.errors || null,
			exception: result.errors || null,
		};
	};

	// Create Mutation
	const [createProjectPosition] = useCreateProjectAdditionalPositionInformationMutation();

	const createPosition = async (position: ProjectPosition): Promise<UpsertResult> => {
		// CREATE
		if (position.service == null) {
			throw new Error(t("validation-error"));
		}

		const vars: CreateProjectAdditionalPositionInformationMutationVariables = {
			hours: position.hours,
			pricePerHour: position.pricePerHour,
			projectId,
			saleableServiceId: position.service.id,
			serviceName: position.service.name,
			servicePrice: position.service.price,
			price: position.price,
			description: position.description,
			accountingProfileId: position.accountingProfile?.id ?? null,
			priceRate: (position.priceRate as ProjectPositionPriceRateType) ?? null,
		};

		const result = await createProjectPosition({ variables: vars });

		// Return the updated position if successful, get the generated id
		const resultData = result.data?.createProjectAdditionalPositionInformation
			?.additionalPositionInformation
			? {
					...position,
					id: result.data?.createProjectAdditionalPositionInformation.additionalPositionInformation
						.id,
				}
			: null;

		return {
			data: resultData,
			errors: result.data?.createProjectAdditionalPositionInformation.errors || null,
			exception: result.errors || null,
		};
	};

	const saveProjectPosition = async (position: ProjectPosition) => {
		setLoading(true);
		try {
			// Update or create
			const result = position.id ? await updatePosition(position) : await createPosition(position);

			if (result.errors && result.errors.length > 0) {
				notifyErrors(result.errors);
				return;
			}

			if (result.exception) {
				notifyException(result.exception);
				return;
			}

			if (!result.data) {
				notifyException(t("no-data-returned-from-upsert"));
				return;
			}

			notifySuccess(t("project-position-updated"));

			onSaved(result.data);
		} catch (error) {
			notifyException(error);
		} finally {
			setLoading(false);
		}
	};

	return {
		saveProjectPosition,
		loading,
	};
}
